<template>
    <kiosk-group-details :teamId="props.teamId" :groupId="props.groupId">
        <div class="col-span-12 lg:col-span-10 flex flex-col w-full max-w-(--breakpoint-lg) justify-center">
            <page-header class="mb-2 md:mb-2">Status Log</page-header>

            <kiosk-group-status-log-filters v-model="fetchParams" @apply="applyFilters" />

            <div v-if="loading && !list.length" class="text-center py-10">
                <vx-icon-loading class="w-9 h-9 animate-loading-spin"></vx-icon-loading>
            </div>

            <template v-if="!loading && !list.length">
                <empty-state>
                    No status changes recorded for this group in this date range. <br />Try widening the range to see
                    more history.
                </empty-state>
            </template>

            <vx-table v-if="list.length" class="lg:grid-cols-3" rowBreakpoint="lg">
                <vx-table-row isHeader>
                    <div>Status</div>
                    <div>Changed by</div>
                    <div>Changed at</div>
                </vx-table-row>

                <kiosk-group-status-log-row v-for="entry in list" :key="entry.id" :modelValue="entry" />
            </vx-table>

            <div v-if="fetchParams.cursor" class="mt-8">
                <vx-button
                    @click="loadData()"
                    color="muted-dark"
                    size="xl"
                    class="grow-0"
                    data-test="load-more-btn"
                    :loading="loading"
                >
                    Load More
                </vx-button>
            </div>
        </div>
    </kiosk-group-details>
</template>

<script lang="ts" setup>
import { onMounted } from 'vue';
import { VxButton, VxTable, VxTableRow, VxIconLoading } from '@voxie/frontend-components';
import KioskGroupDetails from '~/components/kiosk/kiosk-groups/details/KioskGroupDetails.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import EmptyState from '~/components/general/EmptyState.vue';
import KioskGroupStatusLogFilters, { type StatusLogFilters } from './KioskGroupStatusLogFilters.vue';
import KioskGroupStatusLogRow, { type GroupStatusLogEntry } from './KioskGroupStatusLogRow.vue';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { adminGroupStatusLogClient } from '~/services';
import dayjs, { toUTCDateTimeFormat } from '~/utils/dayjs';

const props = defineProps<{
    teamId: number;
    groupId: string;
}>();

const { list, loading, fetchParams, fetchRecords } = useCursorPagination<GroupStatusLogEntry, StatusLogFilters>();

const defaultFilters = (): StatusLogFilters => ({
    start_date: toUTCDateTimeFormat(dayjs().startOf('day').subtract(29, 'day')),
    end_date: toUTCDateTimeFormat(dayjs().endOf('day')),
    sort_dir: 'desc',
    cursor: null,
    per_page: 20,
});

const loadData = () => {
    fetchRecords(adminGroupStatusLogClient.getStatusLog(props.teamId, props.groupId, { ...fetchParams.value }));
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: null,
    };
    loadData();
};

onMounted(() => {
    fetchParams.value = defaultFilters();
    loadData();
});
</script>
